/**
 * Animated microbial-aquatic background. Single full-screen Pixi Filter.
 *
 * Layers (all procedural, no texture samples):
 *   1. Vertical depth gradient (surface → floor)
 *   2. Domain-warped voronoi "membrane" cells — slow flow, two scales
 *   3. Overhead caustics — light banding as if filtered down through water
 *   4. Suspended motes — sparse drifting particles of organic debris
 *   5. Bioluminescent blooms — three slow-moving glow centers
 *   6. Soft vignette
 *
 * Uniforms: uTime (seconds), uResolution (px). Update once per frame.
 */

import { Filter, GlProgram } from 'pixi.js';

const vertex = /* glsl */ `
  in vec2 aPosition;
  out vec2 vTextureCoord;
  uniform vec4 uInputSize;
  uniform vec4 uOutputFrame;
  uniform vec4 uOutputTexture;

  vec4 filterVertexPosition(void) {
      vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
      position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
      position.y = position.y * (2.0*uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
      return vec4(position, 0.0, 1.0);
  }

  vec2 filterTextureCoord(void) {
      return aPosition * (uOutputFrame.zw * uInputSize.zw);
  }

  void main(void) {
      gl_Position = filterVertexPosition();
      vTextureCoord = filterTextureCoord();
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  in vec2 vTextureCoord;
  out vec4 finalColor;

  uniform sampler2D uTexture;
  uniform float uTime;
  uniform vec2 uResolution;

  vec2 hash2(vec2 p) {
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return fract(sin(p) * 43758.5453);
  }

  float hash1(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  float valueNoise(vec2 uv) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float a = hash1(i);
    float b = hash1(i + vec2(1.0, 0.0));
    float c = hash1(i + vec2(0.0, 1.0));
    float d = hash1(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  float fbm(vec2 uv) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      v += valueNoise(uv) * amp;
      uv *= 2.02;
      amp *= 0.5;
    }
    return v;
  }

  float voronoi(vec2 uv, float t) {
    vec2 n = floor(uv);
    vec2 f = fract(uv);
    float minDist = 1.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 neighbor = vec2(float(x), float(y));
        vec2 point = hash2(n + neighbor);
        point = 0.5 + 0.5 * sin(t * 0.15 + 6.2831 * point);
        vec2 diff = neighbor + point - f;
        minDist = min(minDist, length(diff));
      }
    }
    return minDist;
  }

  void main() {
    vec2 screen = vTextureCoord;                     // 0..1
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 p = screen * aspect;
    float t = uTime;

    // --- drifting current (domain warp) ---
    vec2 warp = vec2(
      fbm(p * 1.2 + vec2(t * 0.03, 0.0)),
      fbm(p * 1.2 + vec2(7.3, t * 0.025))
    ) - 0.5;
    vec2 uv = (p + warp * 0.18) * (uResolution.xy / 240.0);

    // --- membrane voronoi (two scales) ---
    float v1 = voronoi(uv, t);
    float v2 = voronoi(uv * 0.42 + vec2(t * 0.02, -t * 0.01), t * 0.7);

    // --- base palette: deep aquatic, darker toward the bottom ---
    float depth  = screen.y;
    vec3 topCol  = vec3(0.025, 0.075, 0.115);
    vec3 botCol  = vec3(0.005, 0.013, 0.032);
    vec3 base    = mix(topCol, botCol, smoothstep(0.0, 1.1, depth));

    vec3 cyanGlow = vec3(0.30, 0.80, 0.95);
    vec3 warmGlow = vec3(0.65, 0.35, 0.75);

    float membrane = smoothstep(0.0, 0.12, v1) * smoothstep(0.55, 0.0, v1);
    float halo     = smoothstep(0.42, 0.0, v2) * 0.55;
    vec3 col = base;
    col += cyanGlow * membrane * 0.26;
    col += warmGlow * halo     * 0.14;

    // --- overhead caustics (three octaves, masked to upper half) ---
    float caust = 0.0;
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      vec2 cp = p * (2.4 + fi * 1.2) + vec2(t * (0.10 + fi * 0.04), t * 0.06);
      float c = sin(cp.x + sin(cp.y + t * 0.25)) * sin(cp.y + cos(cp.x * 0.7 + t * 0.2));
      caust += c * c;
    }
    caust /= 3.0;
    float topMask = smoothstep(1.05, -0.05, depth);
    col += cyanGlow * caust * topMask * 0.28;

    // --- suspended motes (sparse bright specks that drift) ---
    vec2 mp = p * 7.0 + vec2(t * 0.09, t * 0.045);
    float motes = valueNoise(mp) * 0.6 + valueNoise(mp * 1.9 + 3.1) * 0.4;
    float moteMask = smoothstep(0.70, 0.82, motes);
    col += vec3(0.65, 0.95, 1.00) * moteMask * 0.28;

    // --- slow bioluminescent blooms ---
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      vec2 bc = vec2(
        0.5 + 0.32 * sin(t * 0.06 + fi * 2.1),
        0.5 + 0.30 * cos(t * 0.07 + fi * 1.7)
      );
      bc *= aspect;
      float d = distance(p, bc);
      float pulse = 0.55 + 0.45 * sin(t * 0.55 + fi * 1.9);
      vec3 bloomCol = mix(cyanGlow, warmGlow, 0.3 + 0.3 * sin(fi + t * 0.1));
      col += bloomCol * exp(-d * 3.0) * pulse * 0.18;
    }

    // --- slow global shimmer so motion is always visible ---
    float shimmer = 0.5 + 0.5 * sin(t * 0.6 + screen.x * 2.0 + screen.y * 1.5);
    col += cyanGlow * shimmer * 0.012;

    // --- vignette ---
    vec2 c = screen - 0.5;
    float vig = 1.0 - dot(c, c) * 1.3;
    col *= clamp(vig, 0.35, 1.0);

    finalColor = vec4(col, 1.0);
  }
`;

export class VoronoiFilter extends Filter {
  constructor() {
    const glProgram = GlProgram.from({ vertex, fragment, name: 'voronoi-bg' });
    super({
      glProgram,
      resources: {
        uniforms: {
          uTime: { value: 0, type: 'f32' },
          uResolution: { value: [1, 1], type: 'vec2<f32>' },
        },
      },
    });
  }

  update(dt: number, width: number, height: number): void {
    const u = (this.resources.uniforms as any).uniforms;
    u.uTime += dt;
    u.uResolution[0] = width;
    u.uResolution[1] = height;
  }
}
